import { create } from 'zustand'
import { BANNER_SEVERITIES, severityRank, type BannerSeverity } from '../config/banners'
import {
  getRuntimeConfig,
  type BannerDisplay,
  type BannerLink,
  type LocalizedString,
  type RuntimeBanner,
} from '../lib/runtimeConfig'

const DISMISS_STORAGE_KEY = 'helin-dismissed-banners'

export interface ResolvedBanner {
  id: string
  severity: BannerSeverity
  message: LocalizedString
  /** Optional override for the bold severity prefix label. */
  label?: LocalizedString
  dismissible: boolean
  /** Pages this banner targets — '*' means all. */
  pages: string[]
  startsAt?: string
  endsAt?: string
  /** Render mode. Defaults to 'standard'. */
  display: BannerDisplay
  /** Optional inline link rendered inside the message. */
  link?: BannerLink
}

interface BannerState {
  /** All known banners (env fallback + runtime). */
  banners: ResolvedBanner[]
  /** Banner ids the user has dismissed (persisted to localStorage). */
  dismissed: ReadonlySet<string>
  /** Replace the banner pool (called once on app boot, after runtime load). */
  setBanners: (banners: ResolvedBanner[]) => void
  /** Dismiss a banner id. */
  dismiss: (id: string) => void
  /** Returns banners visible on this path right now, sorted highest-severity first. */
  visibleOn: (path: string, now?: Date) => ResolvedBanner[]
}

function readDismissedFromStorage(): Set<string> {
  try {
    const raw = window.localStorage.getItem(DISMISS_STORAGE_KEY)
    if (!raw) return new Set()
    const parsed = JSON.parse(raw) as unknown
    if (!Array.isArray(parsed)) return new Set()
    return new Set(parsed.filter((v): v is string => typeof v === 'string'))
  } catch {
    return new Set()
  }
}

function persistDismissed(set: ReadonlySet<string>): void {
  try {
    window.localStorage.setItem(DISMISS_STORAGE_KEY, JSON.stringify([...set]))
  } catch {
    // Storage full / disabled — silent, dismissal becomes session-only.
  }
}

function pathMatches(banner: ResolvedBanner, path: string): boolean {
  if (banner.pages.length === 0) return true
  if (banner.pages.includes('*')) return true
  return banner.pages.some((entry) => {
    if (entry === path) return true
    if (entry.endsWith('/*')) {
      const prefix = entry.slice(0, -2)
      return path === prefix || path.startsWith(`${prefix}/`)
    }
    return false
  })
}

function inDateRange(banner: ResolvedBanner, now: Date): boolean {
  if (banner.startsAt) {
    const start = new Date(banner.startsAt).getTime()
    if (Number.isFinite(start) && now.getTime() < start) return false
  }
  if (banner.endsAt) {
    const end = new Date(banner.endsAt).getTime()
    if (Number.isFinite(end) && now.getTime() > end) return false
  }
  return true
}

export const useBannerStore = create<BannerState>((set, get) => ({
  banners: [],
  dismissed: readDismissedFromStorage(),

  setBanners: (banners) => set({ banners }),

  dismiss: (id) => {
    const next = new Set(get().dismissed)
    next.add(id)
    persistDismissed(next)
    set({ dismissed: next })
  },

  visibleOn: (path, now = new Date()) => {
    const { banners, dismissed } = get()
    return banners
      .filter((b) => !dismissed.has(b.id))
      .filter((b) => pathMatches(b, path))
      .filter((b) => inDateRange(b, now))
      .sort((a, b) => severityRank(b.severity) - severityRank(a.severity))
  },
}))

function parseEnvBanner(): ResolvedBanner | null {
  const enabled = (import.meta.env.VITE_BANNER_ENABLED as string | undefined)?.toLowerCase()
  if (enabled !== 'true' && enabled !== '1') return null

  const message = (import.meta.env.VITE_BANNER_MESSAGE as string | undefined)?.trim()
  if (!message) return null

  /* v8 ignore start */
  const severityRaw = (import.meta.env.VITE_BANNER_SEVERITY as string | undefined)?.trim()
  const severity = (BANNER_SEVERITIES as readonly string[]).includes(severityRaw ?? '')
    ? (severityRaw as BannerSeverity)
    : 'info'
  /* v8 ignore stop */

  const dismissibleRaw = (import.meta.env.VITE_BANNER_DISMISSIBLE as string | undefined)?.toLowerCase()
  const dismissible = dismissibleRaw === 'false' || dismissibleRaw === '0' ? false : true

  /* v8 ignore start */
  const pagesRaw = ((import.meta.env.VITE_BANNER_PAGES as string | undefined) ?? '').trim()
  /* v8 ignore stop */
  const pages = (pagesRaw.length > 0 ? pagesRaw : '*')
    .split(',')
    .map((p) => p.trim())
    .filter(Boolean)

  const labelRaw = (import.meta.env.VITE_BANNER_LABEL as string | undefined)?.trim()
  const label = labelRaw && labelRaw.length > 0 ? labelRaw : undefined

  return {
    id: (import.meta.env.VITE_BANNER_ID as string | undefined) || 'env-fallback',
    severity,
    message,
    label,
    dismissible,
    pages,
    display: 'standard',
  }
}

function fromRuntime(b: RuntimeBanner): ResolvedBanner {
  const banner: ResolvedBanner = {
    id: b.id,
    severity: b.severity,
    message: b.message,
    dismissible: b.dismissible !== false,
    pages: b.pages && b.pages.length > 0 ? b.pages : ['*'],
    startsAt: b.startsAt,
    endsAt: b.endsAt,
    display: b.display ?? 'standard',
  }
  if (b.label) banner.label = b.label
  if (b.link) banner.link = b.link
  return banner
}

/**
 * Builds the active banner list from runtime + env fallback.
 * Runtime banners win — env banner only shown when runtime has none.
 * Called once after runtime config loads.
 */
export function hydrateBanners(): void {
  const runtime = getRuntimeConfig().banners ?? []
  if (runtime.length > 0) {
    useBannerStore.getState().setBanners(runtime.map(fromRuntime))
    return
  }
  const env = parseEnvBanner()
  useBannerStore.getState().setBanners(env ? [env] : [])
}
